import { COMPANY_NAME } from '@piccolohealth/pbs-common';
import { ChakraV3, stringParam, useQueryParams } from '@piccolohealth/ui';
import { P, z, zodPassword } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaRegCheckCircle } from 'react-icons/fa';
import { Navigate, useNavigate } from 'react-router-dom';
import { HookedFormField } from '../../components/forms/HookedFormField';
import { HookedInput } from '../../components/forms/HookedInput';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { useAuth } from '../../context/AuthContext';
import { asPiccoloError } from '../../utils/errors';
import { zodResolver } from '../../utils/zod';
import { AuthCard } from './components/AuthCard';
import { AuthLayout } from './components/AuthLayout';

type ResendStatus = 'idle' | 'loading' | 'success' | 'error';

type FormValues = {
  password: string;
  confirmPassword: string;
};

const schema: z.ZodSchema<FormValues> = z
  .object({
    password: zodPassword,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  });

export const VerifyInvite = () => {
  const [params] = useQueryParams({
    email: stringParam,
    oobCode: stringParam,
  });
  const { email, oobCode } = params;

  const [resendStatus, setResendStatus] = React.useState<ResendStatus>('idle');

  const { verifyInvite, requestVerifyInviteEmail, logout } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    logout();
  }, [logout]);

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const onResendVerifyInvite = async () => {
    if (!email) {
      return;
    }

    setResendStatus('loading');
    await requestVerifyInviteEmail(email)
      .then(() => setResendStatus('success'))
      .catch(() => {
        setResendStatus('error');
        methods.setError('password', {
          message: 'Failed to resend verification email. Please contact support.',
        });
      });
  };

  const onSubmit = async (data: FormValues) => {
    if (!oobCode || !email) {
      return;
    }

    try {
      await verifyInvite(oobCode, email, data.password);
    } catch (error) {
      const piccoloError = asPiccoloError(error);
      methods.reset({ password: '', confirmPassword: '' });
      methods.setError('password', { message: piccoloError.message });
    }
  };

  const onClickLogin = () => {
    void navigate('/login');
  };

  if (!oobCode || !email) {
    return <Navigate to='/login' />;
  }

  const content = P.run(() => {
    if (resendStatus === 'success') {
      return (
        <AuthCard>
          <ChakraV3.Stack gap={4} align='center'>
            <ChakraV3.Icon color='success' boxSize={16}>
              <FaRegCheckCircle />
            </ChakraV3.Icon>
            <ChakraV3.Text fontSize='sm' textAlign='center'>
              If your account is awaiting verification, we have sent a new verification email.
            </ChakraV3.Text>
          </ChakraV3.Stack>
        </AuthCard>
      );
    }

    if (methods.formState.isSubmitSuccessful) {
      return (
        <AuthCard>
          <ChakraV3.Stack gap={4} align='center'>
            <ChakraV3.Icon color='success' boxSize={16}>
              <FaRegCheckCircle />
            </ChakraV3.Icon>
            <ChakraV3.Text fontSize='sm' textAlign='center'>
              Your password has been set successfully. Please login to continue.
            </ChakraV3.Text>
            <ChakraV3.Button w='full' size='md' colorPalette='brand' onClick={onClickLogin}>
              Login
            </ChakraV3.Button>
          </ChakraV3.Stack>
        </AuthCard>
      );
    }

    return (
      <AuthCard>
        <ChakraV3.Stack gap={0}>
          <ChakraV3.Text fontWeight='semibold' fontSize='2xl'>
            Welcome to {COMPANY_NAME}
          </ChakraV3.Text>
          <ChakraV3.Text fontSize='sm' color='secondary'>
            Please set a password to complete your account setup
          </ChakraV3.Text>
        </ChakraV3.Stack>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Stack gap={4}>
              <HookedFormField name='password' label='Password'>
                <HookedInput
                  name='password'
                  type='password'
                  size='md'
                  placeholder='Enter new password'
                />
              </HookedFormField>
              <HookedFormField name='confirmPassword' label='Confirm Password'>
                <HookedInput
                  name='confirmPassword'
                  type='password'
                  size='md'
                  placeholder='Confirm new password'
                />
              </HookedFormField>
              <HookedSubmitButton
                disabled={false}
                size='md'
                mt={4}
                loadingText='Setting password...'
              >
                Set Password
              </HookedSubmitButton>
            </ChakraV3.Stack>
          </form>
        </FormProvider>
        <ChakraV3.Button
          onClick={onResendVerifyInvite}
          variant='plain'
          w='fit-content'
          cursor='pointer'
          loading={resendStatus === 'loading'}
          loadingText='Resending verification email...'
        >
          Code expired? Resend verification email
        </ChakraV3.Button>
      </AuthCard>
    );
  });

  return <AuthLayout>{content}</AuthLayout>;
};
